import { getStoredToken } from './api.service';

export interface TurnCredentials {
  username: string;
  credential: string;
  ttl: number;
  iceServers: RTCIceServer[];
}

/**
 * SignalingService manages the WebSocket connection to the cloud BE
 * for WebRTC signaling (cloud user ↔ cloud BE ↔ tunnel ↔ local SFU).
 */
export class SignalingService {
  private ws: WebSocket | null = null;
  private nodeId: string;
  private sessionId: string | null = null;
  private turnCredentials: TurnCredentials | null = null;
  private onMessage: ((msg: any) => void) | null = null;
  private onClose: ((code: number, reason: string) => void) | null = null;
  private keepaliveInterval: ReturnType<typeof setInterval> | null = null;
  private pongTimer: ReturnType<typeof setTimeout> | null = null;
  private awaitingPong = false;
  private missedPongs = 0;

  constructor(nodeId: string) {
    this.nodeId = nodeId;
  }

  private startKeepalive(): void {
    this.stopKeepalive();
    this.keepaliveInterval = setInterval(() => {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        this.ws.send(JSON.stringify({ type: 'ping' }));
        this.awaitingPong = true;
        if (this.pongTimer) clearTimeout(this.pongTimer);
        // Tolerate ONE missed pong before declaring the socket dead. On congested
        // low-bandwidth links a pong can easily exceed 10s (bufferbloat) while the
        // connection is still alive — closing on the first miss caused constant
        // reconnect churn for slow-network users.
        this.pongTimer = setTimeout(() => {
          if (!this.awaitingPong || !this.ws) return;
          this.missedPongs++;
          if (this.missedPongs >= 2) {
            console.warn('[Signaling] 2 consecutive pong timeouts — forcing WS close');
            this.ws.close(4000, 'pong_timeout');
          } else {
            console.warn('[Signaling] Pong timeout — tolerating once (slow network?)');
          }
        }, 10000);
      }
    }, 15000); // Ping every 15s (was 25s — faster dead detection)
  }

  private handlePong(): void {
    this.awaitingPong = false;
    this.missedPongs = 0;
    if (this.pongTimer) {
      clearTimeout(this.pongTimer);
      this.pongTimer = null;
    }
  }

  private stopKeepalive(): void {
    if (this.keepaliveInterval) {
      clearInterval(this.keepaliveInterval);
      this.keepaliveInterval = null;
    }
    if (this.pongTimer) {
      clearTimeout(this.pongTimer);
      this.pongTimer = null;
    }
    this.awaitingPong = false;
  }

  connect(
    onMessage: (msg: any) => void,
    onClose?: (code: number, reason: string) => void,
    resumeSessionId?: string
  ): Promise<{
    sessionId: string;
    turnCredentials: TurnCredentials;
    resumed: boolean;
  }> {
    return new Promise((resolve, reject) => {
      const token = getStoredToken();
      if (!token) {
        reject(new Error('Not authenticated'));
        return;
      }

      const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      let wsUrl = `${wsProtocol}//${window.location.host}/ws/signaling?token=${encodeURIComponent(token)}&nodeId=${encodeURIComponent(this.nodeId)}`;
      // Ask the cloud to resume a recent session: same sessionId = the SFU-side
      // peer connection (and the media flowing on it) is preserved.
      if (resumeSessionId) {
        wsUrl += `&resumeSessionId=${encodeURIComponent(resumeSessionId)}`;
      }

      this.ws = new WebSocket(wsUrl);
      this.onMessage = onMessage;
      this.onClose = onClose || null;

      const connectTimeout = setTimeout(() => {
        reject(new Error('Signaling connection timeout'));
        this.ws?.close();
      }, 15000);

      this.ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);

          if (msg.type === 'welcome') {
            clearTimeout(connectTimeout);
            this.sessionId = msg.sessionId;
            this.turnCredentials = msg.turnCredentials;
            this.startKeepalive();
            resolve({
              sessionId: msg.sessionId,
              turnCredentials: msg.turnCredentials,
              resumed: msg.resumed === true,
            });
            return;
          }

          if (msg.type === 'pong') {
            this.handlePong();
            return;
          }

          if (this.onMessage) {
            this.onMessage(msg);
          }
        } catch (err) {
          console.error('Error parsing signaling message:', err);
        }
      };

      this.ws.onclose = (event) => {
        clearTimeout(connectTimeout);
        this.stopKeepalive();
        if (this.onClose) this.onClose(event.code, event.reason);
      };

      this.ws.onerror = (err) => {
        clearTimeout(connectTimeout);
        reject(new Error('Signaling connection failed'));
      };
    });
  }

  send(message: any): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message));
    }
  }

  subscribe(cameraIds: string[]): void {
    this.send({ type: 'subscribe', cameraIds });
  }

  unsubscribe(cameraIds: string[]): void {
    this.send({ type: 'unsubscribe', cameraIds });
  }

  /**
   * Tell the cloud/SFU to drop this session IMMEDIATELY (used on logout).
   * Unlike a plain WS close — which the cloud treats as a transient blip and
   * keeps alive for the resume grace — this asks for the SFU-side peer
   * connection to be reaped now, freeing the UDP port / egress right away.
   */
  closeSession(): void {
    this.send({ type: 'close-session' });
  }

  sendAnswer(sdp: string): void {
    this.send({ type: 'answer', sdp });
  }

  sendIceCandidate(candidate: RTCIceCandidateInit): void {
    this.send({
      type: 'ice-candidate',
      candidate: candidate.candidate,
      sdpMid: candidate.sdpMid,
      sdpMLineIndex: candidate.sdpMLineIndex,
    });
  }

  getSessionId(): string | null {
    return this.sessionId;
  }

  getTurnCredentials(): TurnCredentials | null {
    return this.turnCredentials;
  }

  close(): void {
    this.stopKeepalive();
    // Nullify callbacks BEFORE closing to prevent stale onclose from
    // firing handleSocketClose on a node that's already reconnecting.
    this.onClose = null;
    this.onMessage = null;
    if (this.ws) {
      this.ws.onclose = null;
      this.ws.onerror = null;
      this.ws.onmessage = null;
      this.ws.close();
      this.ws = null;
    }
  }

  getWs(): WebSocket | null {
    return this.ws;
  }
}
